import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getRoleById,
  createRole,
  updateRole,
  updateRolePermissions,
  getAvailableModules,
  getPermissionLevels
} from '../../../api/roles';
import { locationApi } from '../../../api/adminapi';
import { getRoleFromToken } from '../../../utils/jwtUtils';
import './RoleBuilder.css';

function RoleBuilder() {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const isEditMode = !!roleId;

  // Role basic info
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [assignedLocations, setAssignedLocations] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [active, setActive] = useState(true);
  const [isSystemRole, setIsSystemRole] = useState(false);

  // Store original values for system roles (to preserve null vs empty string)
  const [originalRoleData, setOriginalRoleData] = useState(null);

  // Location Master dropdown
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Permission management
  const [modules, setModules] = useState([]);
  const [permissionLevels, setPermissionLevels] = useState([]);
  const [permissions, setPermissions] = useState({});

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [userRole, setUserRole] = useState('');

  // Check if current user is SuperAdmin
  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      const claims = getRoleFromToken(token);
      setUserRole(claims?.role?.toUpperCase() || '');
    }
  }, []);

  const isSuperAdmin = userRole === 'SUPER_ADMIN';

  useEffect(() => {
    loadMetadata();
    loadLocations();
    if (isEditMode) {
      loadRole();
    }
  }, [roleId]);

  const loadMetadata = async () => {
    try {
      const [modulesData, levelsData] = await Promise.all([
        getAvailableModules(),
        getPermissionLevels()
      ]);
      setModules(modulesData);
      setPermissionLevels(levelsData);
    } catch (err) {
      setError('Failed to load metadata: ' + err.message);
    }
  };

  const loadLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await locationApi.getAllActiveLocationNames();
      const locationNames = response?.data || response || [];
      setAvailableLocations(locationNames);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setAvailableLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const loadRole = async () => {
    try {
      setLoading(true);
      const roleData = await getRoleById(roleId);

      // Store original data for system roles (to preserve exact values like null)
      if (roleData.isSystemRole) {
        setOriginalRoleData({
          roleName: roleData.roleName,
          description: roleData.description,
          assignedLocations: roleData.assignedLocations,
          active: roleData.active
        });
      }

      setRoleName(roleData.roleName || '');
      setDescription(roleData.description || '');
      setIsSystemRole(roleData.isSystemRole || false);

      // Parse assignedLocations from comma-separated string to array
      const assignedLocsStr = roleData.assignedLocations || '';
      setAssignedLocations(assignedLocsStr);

      if (assignedLocsStr.trim()) {
        const locsArray = assignedLocsStr.split(',').map(loc => loc.trim()).filter(Boolean);
        setSelectedLocations(locsArray);
      } else {
        setSelectedLocations([]);
      }

      setActive(roleData.active);

      // Permissions are already included in roleData from backend
      // Convert permissions object to the format we need
      const permObj = roleData.permissions || {};
      setPermissions(permObj);

      // For system roles, automatically switch to permissions tab
      if (roleData.isSystemRole) {
        setActiveTab('permissions');
      }

      setError(null);
    } catch (err) {
      setError('Failed to load role: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (moduleName, level) => {
    setPermissions(prev => ({
      ...prev,
      [moduleName]: level
    }));
  };

  const handleRemovePermission = (moduleName) => {
    setPermissions(prev => {
      const newPerms = { ...prev };
      delete newPerms[moduleName];
      return newPerms;
    });
  };

  const handleLocationToggle = (locationName) => {
    setSelectedLocations(prev => {
      if (prev.includes(locationName)) {
        return prev.filter(loc => loc !== locationName);
      } else {
        return [...prev, locationName];
      }
    });
  };

  const handleSelectAllLocations = () => {
    if (selectedLocations.length === availableLocations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations([...availableLocations]);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Validate - for custom roles only
      if (!isSystemRole && !roleName.trim()) {
        alert('Role name is required');
        return;
      }

      // Convert permissions object to array
      const permissionsArray = Object.entries(permissions).map(([module, level]) => ({
        module,
        level
      }));

      if (isEditMode) {
        if (isSystemRole && isSuperAdmin && originalRoleData) {
          // For system roles, SuperAdmin can only update permissions
          // Use original values exactly as received from backend to pass validation
          const roleData = {
            roleName: originalRoleData.roleName,
            description: originalRoleData.description,
            assignedLocations: originalRoleData.assignedLocations,
            active: originalRoleData.active,
            permissions: permissions
          };
          await updateRole(roleId, roleData);
        } else {
          // Convert selectedLocations array to comma-separated string for backend
          const assignedLocsString = selectedLocations.join(', ');

          const roleData = {
            roleName: roleName.trim(),
            description: description.trim(),
            assignedLocations: assignedLocsString,
            active
          };

          await updateRole(roleId, roleData);
          await updateRolePermissions(roleId, permissionsArray);
        }
      } else {
        // Convert selectedLocations array to comma-separated string for backend
        const assignedLocsString = selectedLocations.join(', ');

        const roleData = {
          roleName: roleName.trim(),
          description: description.trim(),
          assignedLocations: assignedLocsString,
          active
        };

        const newRole = await createRole(roleData);
        if (permissionsArray.length > 0) {
          await updateRolePermissions(newRole.id, permissionsArray);
        }
      }

      navigate('/admin/roles');
    } catch (err) {
      setError('Failed to save role: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/roles');
  };

  if (loading && isEditMode) {
    return <div className="role-builder-loading">Loading role...</div>;
  }

  return (
    <div className="role-builder-container">
      <div className="role-builder-header">
        <div>
          <button className="btn-back" onClick={handleCancel}>
            ← Back to Roles
          </button>
          <h1>
            {isEditMode ? (isSystemRole ? 'Edit System Role Permissions' : 'Edit Role') : 'Create New Role'}
            {isSystemRole && <span className="system-role-badge">System Role</span>}
          </h1>
          <p>
            {isSystemRole
              ? 'System roles cannot have their name, description, or locations modified. You can only update permissions.'
              : (isEditMode ? 'Modify role details and permissions' : 'Define a new role with permissions')
            }
          </p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : (isSystemRole ? 'Update Permissions' : (isEditMode ? 'Update Role' : 'Create Role'))}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="role-builder-tabs">
        {!isSystemRole && (
          <button
            className={`tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            Basic Information
          </button>
        )}
        <button
          className={`tab ${activeTab === 'permissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('permissions')}
        >
          Permissions ({Object.keys(permissions).length})
        </button>
      </div>

      {activeTab === 'basic' && !isSystemRole && (
        <div className="tab-content">
          <div className="form-section">
            <h3>Role Details</h3>
            <div className="form-group">
              <label htmlFor="roleName">Role Name *</label>
              <input
                type="text"
                id="roleName"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g., Facility Manager, Regional Admin"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the role's responsibilities and scope"
                rows="4"
              />
            </div>

            <div className="form-group">
              <label htmlFor="assignedLocations">Assigned Locations</label>
              {isLoadingLocations ? (
                <p>Loading locations...</p>
              ) : (
                <div className="location-selector">
                  <div className="location-selector-header">
                    <button
                      type="button"
                      className="btn-select-all"
                      onClick={handleSelectAllLocations}
                    >
                      {selectedLocations.length === availableLocations.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="selected-count">
                      {selectedLocations.length} of {availableLocations.length} selected
                    </span>
                  </div>
                  <div className="location-checkboxes">
                    {availableLocations.length > 0 ? (
                      availableLocations.map(locationName => (
                        <label key={locationName} className="location-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedLocations.includes(locationName)}
                            onChange={() => handleLocationToggle(locationName)}
                          />
                          <span>{locationName}</span>
                        </label>
                      ))
                    ) : (
                      <p className="no-locations">No active locations found. Please add locations in Location Master.</p>
                    )}
                  </div>
                </div>
              )}
              <small className="helper-text">
                Leave empty for all locations, or select specific locations
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                Active (users can be assigned to this role)
              </label>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'permissions' && (
        <div className="tab-content">
          <div className="permissions-section">
            <div className="permissions-header">
              <h3>Module Permissions</h3>
              <p>Assign permission levels for each module</p>
            </div>

            <div className="permissions-matrix">
              <div className="matrix-header">
                <div className="header-cell module-header">Module</div>
                <div className="header-cell level-header">Permission Level</div>
                <div className="header-cell action-header">Action</div>
              </div>

              {modules.map(module => {
                // SYSTEM_LOGIN is a simple toggle - just Enabled or Disabled
                const isToggleModule = module.name === 'SYSTEM_LOGIN';

                return (
                  <div key={module.name} className="matrix-row">
                    <div className="module-cell">
                      <div className="module-name">{module.displayName}</div>
                      <div className="module-description">{module.description}</div>
                    </div>
                    <div className="level-cell">
                      {isToggleModule ? (
                        // Simple toggle for SYSTEM_LOGIN
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={!!permissions[module.name]}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handlePermissionChange(module.name, 'READ');
                              } else {
                                handleRemovePermission(module.name);
                              }
                            }}
                          />
                          <span className="toggle-slider"></span>
                          <span className="toggle-label">
                            {permissions[module.name] ? 'Enabled' : 'Disabled'}
                          </span>
                        </label>
                      ) : (
                        // Full permission dropdown for other modules
                        <select
                          value={permissions[module.name] || ''}
                          onChange={(e) => handlePermissionChange(module.name, e.target.value)}
                          className={permissions[module.name] ? 'has-permission' : ''}
                        >
                          <option value="">No Access</option>
                          {permissionLevels.map(level => (
                            <option key={level.name} value={level.name}>
                              {level.displayName} - {level.description}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="action-cell">
                      {permissions[module.name] && !isToggleModule && (
                        <button
                          className="btn-remove"
                          onClick={() => handleRemovePermission(module.name)}
                          title="Remove permission"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {Object.keys(permissions).length === 0 && (
              <div className="no-permissions">
                <p>No permissions assigned yet. Select permission levels from the dropdowns above.</p>
              </div>
            )}

            <div className="permissions-summary">
              <h4>Permission Summary</h4>
              <div className="summary-grid">
                {Object.entries(permissions).map(([moduleName, level]) => {
                  const module = modules.find(m => m.name === moduleName);
                  const levelObj = permissionLevels.find(l => l.name === level);
                  return (
                    <div key={moduleName} className="summary-item">
                      <span className="summary-module">{module?.displayName}</span>
                      <span className={`summary-level level-${level?.toLowerCase()}`}>
                        {levelObj?.displayName}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleBuilder;
